import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { FiArrowLeft, FiArrowRight, FiExternalLink, FiX } from "react-icons/fi";
import { certificateName } from "./certificateData";

const CertificateViewer = ({ certificates, activeIndex, onNavigate, onClose }) => {
  const dialog = useRef(null);
  const initialTrigger = useRef(document.activeElement);
  const certificate = certificates[activeIndex];

  useEffect(() => {
    const trigger = initialTrigger.current;
    const previousOverflow = document.body.style.overflow;
    const element = dialog.current;
    element.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  const navigate = (step) => onNavigate((activeIndex + step + certificates.length) % certificates.length);

  return createPortal(
    <Dialog
      ref={dialog}
      aria-labelledby="certificate-viewer-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") { event.preventDefault(); navigate(-1); }
        if (event.key === "ArrowRight") { event.preventDefault(); navigate(1); }
      }}
    >
      <Panel>
        <Header>
          <div><p>{certificate.issuer} · {certificate.category}</p><h3 id="certificate-viewer-title">{certificateName(certificate)}</h3></div>
          <button type="button" aria-label="Close certificate preview" onClick={onClose} autoFocus><FiX aria-hidden="true" /></button>
        </Header>
        <ImageArea><img src={certificate.image} alt={`${certificateName(certificate)} certificate awarded to Maximilian Gaber by ${certificate.issuer}`} width={certificate.imageWidth} height={certificate.imageHeight} /></ImageArea>
        <Footer>
          <Navigation aria-label="Certificate navigation">
            <button type="button" aria-label="Previous certificate" disabled={certificates.length < 2} onClick={() => navigate(-1)}><FiArrowLeft aria-hidden="true" /></button>
            <span role="status">{activeIndex + 1} / {certificates.length}</span>
            <button type="button" aria-label="Next certificate" disabled={certificates.length < 2} onClick={() => navigate(1)}><FiArrowRight aria-hidden="true" /></button>
          </Navigation>
          <a href={certificate.pdf || certificate.image} target="_blank" rel="noreferrer">{certificate.pdf ? "Open original PDF" : "Open full-size image"} <FiExternalLink aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span></a>
        </Footer>
      </Panel>
    </Dialog>, document.body
  );
};

export default CertificateViewer;

const Dialog = styled.dialog`
  width: calc(100% - 2rem); max-width: 1040px; max-height: calc(100dvh - 2rem);
  margin: auto; padding: 0; border: 1px solid #dbe6de; border-radius: 1.25rem;
  background: #fff; color: #253d31; box-shadow: 0 24px 100px #0004; overflow: auto;
  &::backdrop { background: #0b1c17ba; backdrop-filter: blur(8px); }
  button, a { &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; } }
  button { display: grid; place-items: center; min-width: 44px; min-height: 44px; border: 1px solid #dbe6de; border-radius: 50%; background: #fff; color: #354b42; cursor: pointer; }
  button:hover:not(:disabled) { background: #edf5ef; }
  button:disabled { opacity: 0.4; cursor: default; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
`;
const Panel = styled.div`min-width: 0;`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 1rem; padding: 1rem;
  p { color: #65736b; font-size: 0.65rem; margin-bottom: 0.4rem; }
  h3 { font-size: clamp(0.95rem, 2vw, 1.25rem); font-weight: 500; line-height: 1.4; }
  button { flex-shrink: 0; font-size: 1.25rem; }
  @media (min-width: 600px) { padding: 1.25rem 1.5rem; }
`;
const ImageArea = styled.div`
  padding: 0.75rem; background: #eef3ef;
  img { display: block; width: 100%; height: auto; max-height: 65dvh; object-fit: contain; }
  @media (min-width: 600px) { padding: 1.5rem; }
`;
const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 1rem;
  a { display: inline-flex; align-items: center; gap: 0.5rem; min-height: 44px; color: #007b61; font-size: 0.75rem; text-decoration: none; }
  a:hover { text-decoration: underline; text-underline-offset: 4px; }
  @media (max-width: 420px) { justify-content: center; }
`;
const Navigation = styled.div`
  display: flex; align-items: center; gap: 0.75rem;
  span { min-width: 4rem; text-align: center; font-size: 0.75rem; color: #65736b; }
`;
